import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { FLabel, FInput, FSelect, FTextarea, CheckBox, YesNo } from "./FormControls";
import { DateField } from "./DateField";
import { Modal, Btn, Empty } from "../../components/UI";
import { Plus, Trash2, Pencil, Lock } from "lucide-react";
import { InfoTip } from "../../components/InfoTip";
import { STATES, COUNTIES, GENDERS, MARITAL, EMPLOYMENT, CLIENT_STATUSES } from "./constants";
import {
  SALUTATIONS, PRONOUNS, ENROLLMENT, ETHNICITIES, SMOKING, LANGUAGES, RELIGIONS, EDUCATION, MILITARY,
  CONTACT_TYPES, RELATIONSHIPS, SCHOOL_CONTACT_TYPES, ENTITY_TABS, COMM_METHODS, PROGRAM_STATUSES, DEMO_DATE_FIELDS,
  STATUS_DATE_RULES, STATUS_DATE_GROUPS, GROUP_UNLOCK_HINT, fieldGroup, STATUS_STAGE_NOTE,
} from "./intakeConstants";

const Opt = (arr) => arr.map((o) => <option key={o} value={o}>{o}</option>);
const Section = ({ title, children }) => (
  <div className="mb-8">
    {title && <h4 className="font-heading text-sm font-bold text-[#3E2723] mb-4">{title}</h4>}
    {children}
  </div>
);

export function IntakeDemographics({ data, set }) {
  const f = (k) => (e) => set({ [k]: e.target.value });
  const setDate = (k) => (v) => set({ program_dates: { ...data.program_dates, [k]: v } });
  const enabledGroups = STATUS_DATE_RULES[data.client_status] || Object.keys(STATUS_DATE_GROUPS);
  const dateEnabled = (fld) => enabledGroups.includes(fieldGroup(fld));
  const statusLabel = (CLIENT_STATUSES.find(([v]) => v === data.client_status) || ["", data.client_status])[1];
  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-x-5 gap-y-5">
        <div><FLabel>First Name</FLabel><FInput data-testid="in-first-name" value={data.first_name} onChange={f("first_name")} /></div>
        <div><FLabel>Middle Name</FLabel><FInput value={data.middle_name} onChange={f("middle_name")} /></div>
        <div><FLabel>Last Name</FLabel><FInput data-testid="in-last-name" value={data.last_name} onChange={f("last_name")} /></div>
        <div><FLabel>Salutation</FLabel><FSelect value={data.salutation} onChange={f("salutation")}>{Opt(SALUTATIONS)}</FSelect></div>
        <div><FLabel>Nickname</FLabel><FInput value={data.nickname} onChange={f("nickname")} /></div>
        <div><FLabel>Pronouns</FLabel><FSelect value={data.pronouns} onChange={f("pronouns")}>{Opt(PRONOUNS)}</FSelect></div>
      </div>
      <div className="mt-5 grid grid-cols-1 md:grid-cols-4 gap-x-5 gap-y-5">
        <div><FLabel>Health Record Number</FLabel><FInput value={data.health_record_number} placeholder="System generated (sequential)" disabled title="Assigned automatically when the intake is saved" /></div>
        <div><FLabel>Social Security Number</FLabel><FInput data-testid="in-ssn" value={data.ssn} onChange={f("ssn")} /></div>
        <div><FLabel>Driver's License Number</FLabel><FInput value={data.drivers_license} onChange={f("drivers_license")} /></div>
        <div><FLabel>Enrollment Status</FLabel><FSelect value={data.enrollment_status} onChange={f("enrollment_status")}>{Opt(ENROLLMENT)}</FSelect></div>
      </div>
      <div className="mt-5 grid grid-cols-1 md:grid-cols-5 gap-x-5 gap-y-5">
        <div><FLabel>Address</FLabel><FInput value={data.address} onChange={f("address")} /></div>
        <div><FLabel>City</FLabel><FInput value={data.city} onChange={f("city")} /></div>
        <div><FLabel>State</FLabel><FSelect value={data.state} onChange={f("state")}>{Opt(STATES)}</FSelect></div>
        <div><FLabel>Zip Code</FLabel><FInput value={data.zip} onChange={f("zip")} /></div>
        <div><FLabel>County</FLabel><FSelect value={data.county} onChange={f("county")}>{Opt(COUNTIES)}</FSelect></div>
        <div><FLabel>Home Phone Number</FLabel><FInput value={data.home_phone} onChange={f("home_phone")} /></div>
        <div><FLabel>Work Number</FLabel><FInput value={data.work_phone} onChange={f("work_phone")} /></div>
        <div><FLabel>Cell Phone Number</FLabel><FInput value={data.cell_phone} onChange={f("cell_phone")} /></div>
        <div className="md:col-span-2"><FLabel>Email</FLabel><FInput data-testid="in-email" type="email" value={data.email} onChange={f("email")} /></div>
      </div>
      <div className="mt-5 grid grid-cols-1 md:grid-cols-4 gap-x-5 gap-y-5">
        <div><FLabel>Gender</FLabel><FSelect data-testid="in-gender" value={data.gender} onChange={f("gender")}>{Opt(GENDERS)}</FSelect></div>
        <div><FLabel>Ethnicity</FLabel><FSelect value={data.ethnicity} onChange={f("ethnicity")}>{Opt(ETHNICITIES)}</FSelect></div>
        <div><FLabel>Date of Birth</FLabel><DateField testId="in-dob" value={data.dob} onChange={(v) => set({ dob: v })} /></div>
        <div><FLabel>Age</FLabel><FInput value={data.age} onChange={f("age")} /></div>
        <div><FLabel>Weight (Pounds)</FLabel><FInput value={data.weight} onChange={f("weight")} /></div>
        <div><FLabel>Height (Ft ' in)</FLabel><FInput value={data.height} onChange={f("height")} /></div>
        <div><FLabel>Smoking Status</FLabel><FSelect value={data.smoking_status} onChange={f("smoking_status")}>{Opt(SMOKING)}</FSelect></div>
        <div><FLabel>Preferred Language</FLabel><FSelect value={data.preferred_language} onChange={f("preferred_language")}>{Opt(LANGUAGES)}</FSelect></div>
        <div><FLabel>Marital Status</FLabel><FSelect value={data.marital_status} onChange={f("marital_status")}>{Opt(MARITAL)}</FSelect></div>
        <div><FLabel>Religion</FLabel><FSelect value={data.religion} onChange={f("religion")}>{Opt(RELIGIONS)}</FSelect></div>
        <div><FLabel>Education Level</FLabel><FSelect value={data.education_level} onChange={f("education_level")}>{Opt(EDUCATION)}</FSelect></div>
        <div><FLabel>Employment</FLabel><FSelect value={data.employment} onChange={f("employment")}>{Opt(EMPLOYMENT)}</FSelect></div>
        <div><FLabel>Military</FLabel><FSelect value={data.military} onChange={f("military")}>{Opt(MILITARY)}</FSelect></div>
      </div>
      <Section title="">
        <div className="mt-6 rounded-2xl border border-[#EFEBE0] bg-[#FDFCF8] p-5">
          <h4 className="font-heading text-sm font-bold text-[#3E2723] mb-1 flex items-center gap-1.5">Program & Screening Dates
            <InfoTip text="Following episode-of-care standards used by leading EHRs, date fields activate progressively as the Client Status advances: referral dates first, then screening and intake at Pre-Admit, then admission, treatment, and discharge at Active. Values entered earlier are always preserved." />
          </h4>
          <p className="mb-4 text-xs text-[#7B6157]" data-testid="program-dates-stage-note">
            <span className="font-bold text-[#5D4037]">Stage: {statusLabel}.</span> {STATUS_STAGE_NOTE[data.client_status] || ""}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-x-5 gap-y-5">
            {DEMO_DATE_FIELDS.map((fld) => {
              const on = dateEnabled(fld);
              const slug = fld.toLowerCase().replace(/[^a-z0-9]+/g, "-");
              return (
                <div key={fld} className={on ? "" : "opacity-70"} data-testid={`pd-wrap-${slug}`}>
                  <FLabel>{fld}{!on && <Lock size={10} className="ml-1 inline text-[#84675B]" aria-label="Locked at this status" />}</FLabel>
                  <DateField placeholder="MM/DD/YYYY" testId={`pd-${slug}`} value={data.program_dates[fld] || ""} onChange={setDate(fld)}
                    disabled={!on} disabledHint={`Activates when Client Status is ${GROUP_UNLOCK_HINT[fieldGroup(fld)] || "a later stage"}`} />
                  {!on && data.program_dates[fld] && <p className="mt-1 text-[10px] text-[#84675B]">Recorded at an earlier stage - preserved</p>}
                </div>
              );
            })}
          </div>
        </div>
      </Section>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-5">
          <div><FLabel>State Custody</FLabel><YesNo value={data.state_custody} onChange={(v) => set({ state_custody: v })} testId="in-custody" /></div>
          <div><FLabel>Literate</FLabel><YesNo value={data.literate} onChange={(v) => set({ literate: v })} testId="in-literate" /></div>
        </div>
        <div>
          <FLabel>Client Status</FLabel>
          <div className="flex flex-wrap gap-2 mt-1">
            {CLIENT_STATUSES.map(([v, l]) => (
              <CheckBox key={v} checked={data.client_status === v} onChange={() => set({ client_status: v })} label={l} testId={`in-status-${v}`} />
            ))}
          </div>
        </div>
      </div>
      <div className="mt-6 flex flex-wrap gap-3">
        <CheckBox checked={data.transition_from_provider} onChange={() => set({ transition_from_provider: !data.transition_from_provider })} label="Transition of Care from another Provider" testId="in-transition-from" />
        <CheckBox checked={data.transition_to_provider} onChange={() => set({ transition_to_provider: !data.transition_to_provider })} label="Transition of Care to another Provider" testId="in-transition-to" />
        <CheckBox checked={data.deceased} onChange={() => set({ deceased: !data.deceased })} label="Deceased" testId="in-deceased" />
      </div>
    </div>
  );
}

export function EmployerSchool({ data, set }) {
  const f = (k) => (e) => set({ [k]: e.target.value });
  const [modal, setModal] = useState(false);
  const [c, setC] = useState({ name: "", type: "", email: "" });
  return (
    <div>
      <Section title="School Information">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-5">
          <div><FLabel>School</FLabel><FInput data-testid="school-name" value={data.school} onChange={f("school")} /></div>
          <div><FLabel>Student ID</FLabel><FInput value={data.student_id} onChange={f("student_id")} /></div>
        </div>
        <div className="mt-5 grid grid-cols-1 md:grid-cols-5 gap-x-5 gap-y-5">
          <div><FLabel>Address</FLabel><FInput value={data.address} onChange={f("address")} /></div>
          <div><FLabel>City</FLabel><FInput value={data.city} onChange={f("city")} /></div>
          <div><FLabel>State</FLabel><FSelect value={data.state} onChange={f("state")}>{Opt(STATES)}</FSelect></div>
          <div><FLabel>Zip Code</FLabel><FInput value={data.zip} onChange={f("zip")} /></div>
          <div><FLabel>County</FLabel><FSelect value={data.county} onChange={f("county")}>{Opt(COUNTIES)}</FSelect></div>
          <div className="md:col-span-3"><FLabel>District Name</FLabel><FInput value={data.district_name} onChange={f("district_name")} /></div>
          <div className="md:col-span-2"><FLabel>District ID</FLabel><FInput value={data.district_id} onChange={f("district_id")} /></div>
        </div>
      </Section>
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-heading text-sm font-bold text-[#3E2723]">School Contacts/Resources</h4>
        <button type="button" data-testid="add-school-contact-btn" onClick={() => { setC({ name: "", type: "", email: "" }); setModal(true); }}
          className="rounded-lg border border-[#E5DFD3] px-4 py-2.5 text-sm font-medium text-[#4E342E] hover:bg-[#F5F2EB]">Add New People</button>
      </div>
      <div className="rounded-xl border border-[#EFEBE0] overflow-hidden">
        <table className="w-full text-sm text-left">
          <thead><tr>{["Name", "Type", "Email", ""].map((h, i) => <th key={i} className="border-b border-[#EFEBE0] bg-[#FDFCF8] py-3 px-4 font-semibold text-[#3E2723]">{h}</th>)}</tr></thead>
          <tbody>
            {data.contacts.map((ct, i) => (
              <tr key={i}>
                <td className="border-b border-[#EFEBE0] py-3 px-4">{ct.name}</td>
                <td className="border-b border-[#EFEBE0] py-3 px-4">{ct.type}</td>
                <td className="border-b border-[#EFEBE0] py-3 px-4">{ct.email}</td>
                <td className="border-b border-[#EFEBE0] py-3 px-4 text-right">
                  <button type="button" data-testid={`del-school-contact-${i}`} aria-label="Delete" onClick={() => set({ contacts: data.contacts.filter((_, j) => j !== i) })} className="h-8 w-8 rounded-lg bg-[#FFEBEE] text-[#C62828] inline-flex items-center justify-center"><Trash2 size={13} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.contacts.length === 0 && <Empty text="No school contacts added" />}
      </div>
      <Modal open={modal} onClose={() => setModal(false)} title="Add School Contact">
        <div className="space-y-4">
          <div><FLabel>Name</FLabel><FInput data-testid="school-contact-name" value={c.name} onChange={(e) => setC({ ...c, name: e.target.value })} /></div>
          <div><FLabel>Type</FLabel><FSelect data-testid="school-contact-type" value={c.type} onChange={(e) => setC({ ...c, type: e.target.value })}>{Opt(SCHOOL_CONTACT_TYPES)}</FSelect></div>
          <div><FLabel>Email</FLabel><FInput data-testid="school-contact-email" type="email" value={c.email} onChange={(e) => setC({ ...c, email: e.target.value })} /></div>
          <div className="flex justify-end gap-2"><Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn>
            <Btn data-testid="school-contact-save" onClick={() => { if (c.name) { set({ contacts: [...data.contacts, c] }); setModal(false); } }}>Add</Btn></div>
        </div>
      </Modal>
    </div>
  );
}

const ENTITY_EMPTY = { name: "", relationship: "", health_record_number: "", address: "", city: "", state: "", zip: "", phone: "", email: "" };

export function ExternalEntities({ data, set }) {
  const [tab, setTab] = useState("family");
  const [modal, setModal] = useState(false);
  const [e, setE] = useState(ENTITY_EMPTY);
  const [editIdx, setEditIdx] = useState(null);
  const list = data[tab] || [];
  const label = ENTITY_TABS.find(([k]) => k === tab)[1];
  const saveEntity = () => {
    if (!e.name) return;
    const next = editIdx === null ? [...list, e] : list.map((x, i) => (i === editIdx ? e : x));
    set({ [tab]: next }); setModal(false);
  };
  return (
    <div>
      <div className="flex mb-5 overflow-x-auto">
        {ENTITY_TABS.map(([v, l]) => (
          <button key={v} type="button" data-testid={`entity-tab-${v}`} onClick={() => setTab(v)}
            className={`whitespace-nowrap px-5 py-2.5 text-sm font-medium transition-colors first:rounded-l-lg last:rounded-r-lg ${tab === v ? "bg-[#D9C7B2] text-[#3E2723]" : "bg-[#F1EEE7] text-[#84675B]"}`}>{l}</button>
        ))}
      </div>
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-heading text-sm font-bold text-[#3E2723]">{label} Contacts</h4>
        <button type="button" data-testid="add-entity-btn" onClick={() => { setE(ENTITY_EMPTY); setEditIdx(null); setModal(true); }}
          className="rounded-lg border border-[#E5DFD3] px-4 py-2.5 text-sm font-medium text-[#4E342E] hover:bg-[#F5F2EB]">Add New {label === "Family" ? "Family Member" : "Contact"}</button>
      </div>
      <div className="rounded-xl border border-[#EFEBE0] overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead><tr>{["Name", "Relationship", "Record #", "Address", "Phone", "Email", ""].map((h, i) => <th key={i} className="border-b border-[#EFEBE0] bg-[#FDFCF8] py-3 px-4 font-semibold text-[#3E2723] whitespace-nowrap">{h}</th>)}</tr></thead>
          <tbody>
            {list.map((x, i) => (
              <tr key={i}>
                <td className="border-b border-[#EFEBE0] py-3 px-4 font-medium">{x.name}</td>
                <td className="border-b border-[#EFEBE0] py-3 px-4">{x.relationship}</td>
                <td className="border-b border-[#EFEBE0] py-3 px-4">{x.health_record_number || "-"}</td>
                <td className="border-b border-[#EFEBE0] py-3 px-4">{[x.address, x.city, x.state].filter(Boolean).join(", ") || "-"}</td>
                <td className="border-b border-[#EFEBE0] py-3 px-4">{x.phone}</td>
                <td className="border-b border-[#EFEBE0] py-3 px-4">{x.email}</td>
                <td className="border-b border-[#EFEBE0] py-3 px-4">
                  <div className="flex gap-2">
                    <button type="button" data-testid={`edit-entity-${i}`} aria-label="Edit" onClick={() => { setE(list[i]); setEditIdx(i); setModal(true); }} className="h-8 w-8 rounded-lg bg-[#FBE9E7] text-[#7B6157] inline-flex items-center justify-center"><Pencil size={13} /></button>
                    <button type="button" data-testid={`del-entity-${i}`} aria-label="Delete" onClick={() => set({ [tab]: list.filter((_, j) => j !== i) })} className="h-8 w-8 rounded-lg bg-[#FFEBEE] text-[#C62828] inline-flex items-center justify-center"><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {list.length === 0 && <Empty text={`No ${label.toLowerCase()} contacts added`} />}
      </div>
      <Modal open={modal} onClose={() => setModal(false)} title={`${editIdx === null ? "Add" : "Edit"} ${label} Contact`} wide>
        <div className="grid grid-cols-2 gap-4">
          <div><FLabel>Name</FLabel><FInput data-testid="entity-name" value={e.name} onChange={(ev) => setE({ ...e, name: ev.target.value })} /></div>
          <div><FLabel>Relationship to Client</FLabel><FSelect data-testid="entity-relationship" value={e.relationship} onChange={(ev) => setE({ ...e, relationship: ev.target.value })}>{Opt(RELATIONSHIPS)}</FSelect></div>
          <div><FLabel>Health Record Number</FLabel><FInput value={e.health_record_number} onChange={(ev) => setE({ ...e, health_record_number: ev.target.value })} /></div>
          <div><FLabel>Phone</FLabel><FInput value={e.phone} onChange={(ev) => setE({ ...e, phone: ev.target.value })} /></div>
          <div className="col-span-2"><FLabel>Address</FLabel><FInput value={e.address} onChange={(ev) => setE({ ...e, address: ev.target.value })} /></div>
          <div><FLabel>City</FLabel><FInput value={e.city} onChange={(ev) => setE({ ...e, city: ev.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><FLabel>State</FLabel><FSelect value={e.state} onChange={(ev) => setE({ ...e, state: ev.target.value })}>{Opt(STATES)}</FSelect></div>
            <div><FLabel>Zip</FLabel><FInput value={e.zip} onChange={(ev) => setE({ ...e, zip: ev.target.value })} /></div>
          </div>
          <div className="col-span-2"><FLabel>Email</FLabel><FInput type="email" value={e.email} onChange={(ev) => setE({ ...e, email: ev.target.value })} /></div>
          <div className="col-span-2 flex justify-end gap-2"><Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn>
            <Btn data-testid="entity-save" onClick={saveEntity}>{editIdx === null ? "Add" : "Save"}</Btn></div>
        </div>
      </Modal>
    </div>
  );
}

export function EmergencySection({ data, set }) {
  const setRow = (i, k, v) => set({ contacts: data.contacts.map((r, j) => (j === i ? { ...r, [k]: v } : r)) });
  return (
    <div>
      <h4 className="font-heading text-sm font-bold text-[#3E2723] mb-4">In Case of Emergency Contact</h4>
      {data.contacts.map((r, i) => (
        <div key={i} className="rounded-2xl border border-[#EFEBE0] bg-[#FDFCF8] p-4 mb-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-x-5 gap-y-4">
            <div><FLabel>Name</FLabel><FInput data-testid={`em-name-${i}`} value={r.name} onChange={(e) => setRow(i, "name", e.target.value)} /></div>
            <div><FLabel>Relationship</FLabel><FSelect value={r.relationship} onChange={(e) => setRow(i, "relationship", e.target.value)}>{Opt(RELATIONSHIPS)}</FSelect></div>
            <div><FLabel>Home Phone Number</FLabel><FInput value={r.home_phone} onChange={(e) => setRow(i, "home_phone", e.target.value)} /></div>
            <div><FLabel>Cell Phone Number</FLabel><FInput value={r.cell_phone} onChange={(e) => setRow(i, "cell_phone", e.target.value)} /></div>
            <div><FLabel>Contact Type</FLabel><FSelect value={r.contact_type} onChange={(e) => setRow(i, "contact_type", e.target.value)}>{Opt(CONTACT_TYPES)}</FSelect></div>
            <div><FLabel>Number</FLabel><FInput value={r.number} onChange={(e) => setRow(i, "number", e.target.value)} /></div>
            <div className="md:col-span-2 flex items-end justify-end">
              {data.contacts.length > 1 && (
                <button type="button" data-testid={`em-remove-${i}`} onClick={() => set({ contacts: data.contacts.filter((_, j) => j !== i) })}
                  className="h-11 w-11 rounded-xl border border-[#E5DFD3] text-[#84675B] inline-flex items-center justify-center hover:bg-[#FFEBEE] hover:text-[#C62828]"><Trash2 size={16} /></button>
              )}
            </div>
          </div>
        </div>
      ))}
      <button type="button" data-testid="em-add-more" onClick={() => set({ contacts: [...data.contacts, { name: "", relationship: "", home_phone: "", cell_phone: "", contact_type: "", number: "" }] })}
        className="mt-1 flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-[#C9BEB0] px-5 py-3 text-sm font-semibold text-[#6D4C41] hover:bg-[#F5F2EB]"><Plus size={15} /> Add More</button>
      <div className="mt-6"><FLabel>Note</FLabel><FTextarea rows={4} value={data.note} onChange={(e) => set({ note: e.target.value })} /></div>
    </div>
  );
}

export function PermissionToCommunicate({ data, set }) {
  const setRow = (i, patch) => set({ entries: data.entries.map((r, j) => (j === i ? { ...r, ...patch } : r)) });
  return (
    <div>
      <h4 className="font-heading text-sm font-bold text-[#3E2723] mb-1">Permission to Communicate</h4>
      <p className="text-sm text-[#84675B] mb-4">Who may we contact about this client, and how?</p>
      {data.entries.map((r, i) => (
        <div key={i} className="rounded-2xl border border-[#EFEBE0] bg-[#FDFCF8] p-4 mb-3">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1.4fr_auto] gap-x-5 gap-y-4 items-end">
            <div><FLabel>Name</FLabel><FInput data-testid={`perm-name-${i}`} value={r.name} onChange={(e) => setRow(i, { name: e.target.value })} /></div>
            <div><FLabel>Relationship</FLabel><FSelect value={r.relationship} onChange={(e) => setRow(i, { relationship: e.target.value })}>{Opt(RELATIONSHIPS)}</FSelect></div>
            <div>
              <FLabel>Permitted Methods</FLabel>
              <div className="flex flex-wrap gap-2 mt-1">
                {COMM_METHODS.map((m) => (
                  <CheckBox key={m} checked={r.methods.includes(m)} label={m} testId={`perm-${i}-${m.toLowerCase()}`}
                    onChange={() => setRow(i, { methods: r.methods.includes(m) ? r.methods.filter((x) => x !== m) : [...r.methods, m] })} />
                ))}
              </div>
            </div>
            <div>
              {data.entries.length > 1 && (
                <button type="button" data-testid={`perm-remove-${i}`} onClick={() => set({ entries: data.entries.filter((_, j) => j !== i) })}
                  className="h-11 w-11 rounded-xl border border-[#E5DFD3] text-[#84675B] inline-flex items-center justify-center hover:bg-[#FFEBEE] hover:text-[#C62828]"><Trash2 size={16} /></button>
              )}
            </div>
          </div>
        </div>
      ))}
      <button type="button" data-testid="perm-add-more" onClick={() => set({ entries: [...data.entries, { name: "", relationship: "", methods: [] }] })}
        className="mt-1 flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-[#C9BEB0] px-5 py-3 text-sm font-semibold text-[#6D4C41] hover:bg-[#F5F2EB]"><Plus size={15} /> Add More</button>
      <div className="mt-6"><FLabel>Note</FLabel><FTextarea rows={4} value={data.note} onChange={(e) => set({ note: e.target.value })} /></div>
    </div>
  );
}

export function CallPortal({ data, set }) {
  const f = (k) => (e) => set({ [k]: e.target.value });
  return (
    <div>
      <h4 className="font-heading text-sm font-bold text-[#3E2723] mb-4">Contact Person - Person Calling on behalf of Client, if other than self</h4>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-5 gap-y-5">
        <div><FLabel>Contact Person</FLabel><FInput data-testid="portal-contact" value={data.contact_person} onChange={f("contact_person")} /></div>
        <div><FLabel>Relationship to Person</FLabel><FSelect value={data.relationship} onChange={f("relationship")}>{Opt(RELATIONSHIPS)}</FSelect></div>
        <div />
        <div><FLabel>Home Phone Number</FLabel><FInput value={data.home_phone} onChange={f("home_phone")} /></div>
        <div><FLabel>Cell Phone Number</FLabel><FInput value={data.cell_phone} onChange={f("cell_phone")} /></div>
        <div><FLabel>Alternate Home Number</FLabel><FInput value={data.alt_home_phone} onChange={f("alt_home_phone")} /></div>
      </div>
      <div className="mt-5 grid grid-cols-1 md:grid-cols-5 gap-x-5 gap-y-5">
        <div><FLabel>Address</FLabel><FInput value={data.address} onChange={f("address")} /></div>
        <div><FLabel>City</FLabel><FInput value={data.city} onChange={f("city")} /></div>
        <div><FLabel>State</FLabel><FSelect value={data.state} onChange={f("state")}>{Opt(STATES)}</FSelect></div>
        <div><FLabel>Zip Code</FLabel><FInput value={data.zip} onChange={f("zip")} /></div>
        <div><FLabel>County</FLabel><FSelect value={data.county} onChange={f("county")}>{Opt(COUNTIES)}</FSelect></div>
      </div>
      <div className="mt-5 max-w-md"><FLabel>E-mail</FLabel><FInput type="email" value={data.email} onChange={f("email")} /></div>
      <div className="mt-5"><FLabel>Notes</FLabel><FTextarea rows={5} value={data.notes} onChange={f("notes")} /></div>
    </div>
  );
}

const PROGRAM_EMPTY = { program_name: "", location: "", incident: "", start_date: "", end_date: "", contact_date: "", team: "", provider: "", status: "", screening_date: "", intake_date: "", treatment_plan_date: "", admission_date: "", discharge_date: "", auth_confirmation: "" };
const PROGRAM_DATE_COLS = [["contact_date", "Contact Date"], ["screening_date", "Screening Date"], ["intake_date", "Intake Date"], ["treatment_plan_date", "Treatment Plan Date"], ["admission_date", "Admission Date"], ["discharge_date", "Discharge Date"]];
const LOS_EMPTY = { level_of_service: "Inpt", type_of_service: "", care_code: "", care_description: "", service_class_code: "", service_groups: "", service_description: "", initial_max_auth: "", initial_max_units: "", concurrent_max_auth: "", concurrent_max_units: "", max_daily_units: "", approved: "No" };
const LOS_LEVELS = ["Inpt", "Outpt", "Res", "PHP", "IOP", "Crisis"];
const LOS_TYPES = ["MH", "SU", "DD", "MHSU", "MH, MHSU"];
const LOS_FIELDS = [
  ["type_of_service", "Type of Service", "select", LOS_TYPES],
  ["care_code", "Type of Care Code", "text"], ["care_description", "Type of Care Description", "text"],
  ["service_class_code", "Service Class Code", "text"], ["service_groups", "Service Groups Available", "text"],
  ["initial_max_auth", "Initial Max Auth Length", "text"], ["initial_max_units", "Initial Max Units Auth'd", "text"],
  ["concurrent_max_auth", "Concurrent Max Auth Length", "text"], ["concurrent_max_units", "Concurrent Max Units Auth'd", "text"],
  ["max_daily_units", "Max Daily Units", "text"], ["approved", "Approved", "select", ["Yes", "No", "Pending"]],
];

export function AuthorizedPrograms({ data, set }) {
  const [modal, setModal] = useState(false);
  const [p, setP] = useState(PROGRAM_EMPTY);
  const [editIdx, setEditIdx] = useState(null);
  const [programList, setProgramList] = useState([]);
  const [addingNew, setAddingNew] = useState(false);
  const [newProg, setNewProg] = useState("");
  const [losModal, setLosModal] = useState(false);
  const [los, setLos] = useState(LOS_EMPTY);
  const [losEditIdx, setLosEditIdx] = useState(null);
  const levels = data.levels_of_service || [];

  const loadPrograms = () => api.get("/programs").then((r) => setProgramList(r.data.map((x) => x.name))).catch(() => {});
  useEffect(() => { if (modal) loadPrograms(); }, [modal]); // eslint-disable-line react-hooks/exhaustive-deps

  const createProgram = async () => {
    const name = newProg.trim();
    if (!name) return;
    try {
      await api.post("/programs", { name });
      toast.success(`Program "${name}" created`);
    } catch (err) {
      if (!apiError(err).includes("already exists")) { toast.error(apiError(err)); return; }
    }
    setP((s) => ({ ...s, program_name: name }));
    setNewProg(""); setAddingNew(false); loadPrograms();
  };

  const saveProgram = () => {
    if (!p.program_name) { toast.error("Select a program"); return; }
    const next = editIdx === null ? [...data.programs, p] : data.programs.map((x, i) => (i === editIdx ? p : x));
    set({ programs: next }); setModal(false);
  };
  const saveLos = () => {
    if (!los.service_description && !los.care_code) { toast.error("Enter at least a care code or service description"); return; }
    const next = losEditIdx === null ? [...levels, los] : levels.map((x, i) => (i === losEditIdx ? los : x));
    set({ levels_of_service: next }); setLosModal(false);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-heading text-sm font-bold text-[#3E2723]">Authorized Programs</h4>
        <button type="button" data-testid="add-program-btn" onClick={() => { setP(PROGRAM_EMPTY); setEditIdx(null); setAddingNew(false); setModal(true); }}
          className="rounded-lg border border-[#E5DFD3] px-4 py-2.5 text-sm font-medium text-[#4E342E] hover:bg-[#F5F2EB]">Add Programs/Services</button>
      </div>
      <div className="rounded-xl border border-[#EFEBE0] overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead><tr>{["Program Name", "Location", "Incident", "Start Date", "End Date", "Contact Date", "Team", "Provider", "Program Status", "Screening Date", "Intake Date", "Treatment Plan Date", "Admission Date", "Discharge Date", "Auth #", ""].map((h, i) => <th key={i} className="border-b border-[#EFEBE0] bg-[#FDFCF8] py-3 px-4 font-semibold text-[#3E2723] whitespace-nowrap">{h}</th>)}</tr></thead>
          <tbody>
            {data.programs.map((x, i) => (
              <tr key={i}>
                <td className="border-b border-[#EFEBE0] py-3 px-4 font-medium whitespace-nowrap">{x.program_name}</td>
                <td className="border-b border-[#EFEBE0] py-3 px-4">{x.location || "-"}</td>
                <td className="border-b border-[#EFEBE0] py-3 px-4">{x.incident || "-"}</td>
                <td className="border-b border-[#EFEBE0] py-3 px-4 whitespace-nowrap">{x.start_date || "-"}</td>
                <td className="border-b border-[#EFEBE0] py-3 px-4 whitespace-nowrap">{x.end_date || "-"}</td>
                <td className="border-b border-[#EFEBE0] py-3 px-4 whitespace-nowrap">{x.contact_date || "-"}</td>
                <td className="border-b border-[#EFEBE0] py-3 px-4">{x.team || "-"}</td>
                <td className="border-b border-[#EFEBE0] py-3 px-4">{x.provider || "-"}</td>
                <td className="border-b border-[#EFEBE0] py-3 px-4">{x.status || "-"}</td>
                <td className="border-b border-[#EFEBE0] py-3 px-4 whitespace-nowrap">{x.screening_date || "-"}</td>
                <td className="border-b border-[#EFEBE0] py-3 px-4 whitespace-nowrap">{x.intake_date || "-"}</td>
                <td className="border-b border-[#EFEBE0] py-3 px-4 whitespace-nowrap">{x.treatment_plan_date || "-"}</td>
                <td className="border-b border-[#EFEBE0] py-3 px-4 whitespace-nowrap">{x.admission_date || "-"}</td>
                <td className="border-b border-[#EFEBE0] py-3 px-4 whitespace-nowrap">{x.discharge_date || "-"}</td>
                <td className="border-b border-[#EFEBE0] py-3 px-4">{x.auth_confirmation || "-"}</td>
                <td className="border-b border-[#EFEBE0] py-3 px-4">
                  <div className="flex gap-2">
                    <button type="button" data-testid={`edit-program-${i}`} aria-label="Edit" onClick={() => { setP({ ...PROGRAM_EMPTY, ...data.programs[i] }); setEditIdx(i); setAddingNew(false); setModal(true); }} className="h-8 w-8 rounded-lg bg-[#FBE9E7] text-[#7B6157] inline-flex items-center justify-center"><Pencil size={13} /></button>
                    <button type="button" data-testid={`del-program-${i}`} aria-label="Delete" onClick={() => set({ programs: data.programs.filter((_, j) => j !== i) })} className="h-8 w-8 rounded-lg bg-[#FFEBEE] text-[#C62828] inline-flex items-center justify-center"><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.programs.length === 0 && <Empty text="No authorized programs yet" />}
      </div>

      <div className="mt-8 flex items-center justify-between mb-3">
        <h4 className="font-heading text-sm font-bold text-[#3E2723]">Level of Service: Inpatient &amp; Higher Level of Care (HLOC)</h4>
        <button type="button" data-testid="add-los-btn" onClick={() => { setLos(LOS_EMPTY); setLosEditIdx(null); setLosModal(true); }}
          className="rounded-lg border border-[#E5DFD3] px-4 py-2.5 text-sm font-medium text-[#4E342E] hover:bg-[#F5F2EB]">Add New Level of Service</button>
      </div>
      <div className="rounded-xl border border-[#EFEBE0] overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead><tr>{["Level of Service", "Type of Service", "Type of Care Code", "Type of Care Description", "Service Class Code", "Service Groups Available", "Service Description", "Initial Max Auth Length", "Initial Max Units Auth'd", "Concurrent Max Auth Length", "Concurrent Max Units Auth'd", "Max Daily Units", "Approved", ""].map((h, i) => <th key={i} className="border-b border-[#EFEBE0] bg-[#FDFCF8] py-3 px-4 font-semibold text-[#3E2723] whitespace-nowrap">{h}</th>)}</tr></thead>
          <tbody>
            {levels.map((x, i) => (
              <tr key={i} data-testid={`los-row-${i}`}>
                <td className="border-b border-[#EFEBE0] py-3 px-4 font-medium">{x.level_of_service}</td>
                <td className="border-b border-[#EFEBE0] py-3 px-4 whitespace-nowrap">{x.type_of_service || "-"}</td>
                <td className="border-b border-[#EFEBE0] py-3 px-4">{x.care_code || "-"}</td>
                <td className="border-b border-[#EFEBE0] py-3 px-4">{x.care_description || "-"}</td>
                <td className="border-b border-[#EFEBE0] py-3 px-4">{x.service_class_code || "-"}</td>
                <td className="border-b border-[#EFEBE0] py-3 px-4">{x.service_groups || "-"}</td>
                <td className="border-b border-[#EFEBE0] py-3 px-4 whitespace-nowrap">{x.service_description || "-"}</td>
                <td className="border-b border-[#EFEBE0] py-3 px-4">{x.initial_max_auth || "-"}</td>
                <td className="border-b border-[#EFEBE0] py-3 px-4">{x.initial_max_units || "-"}</td>
                <td className="border-b border-[#EFEBE0] py-3 px-4">{x.concurrent_max_auth || "-"}</td>
                <td className="border-b border-[#EFEBE0] py-3 px-4">{x.concurrent_max_units || "-"}</td>
                <td className="border-b border-[#EFEBE0] py-3 px-4">{x.max_daily_units || "-"}</td>
                <td className="border-b border-[#EFEBE0] py-3 px-4">
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${x.approved === "Yes" ? "bg-[#E8F5E9] text-[#2E7D32]" : x.approved === "Pending" ? "bg-[#FFF3E0] text-[#B84A00]" : "bg-[#FFEBEE] text-[#C62828]"}`}>{x.approved}</span>
                </td>
                <td className="border-b border-[#EFEBE0] py-3 px-4">
                  <div className="flex gap-2">
                    <button type="button" data-testid={`edit-los-${i}`} aria-label="Edit level of service" onClick={() => { setLos({ ...LOS_EMPTY, ...levels[i] }); setLosEditIdx(i); setLosModal(true); }} className="h-8 w-8 rounded-lg bg-[#FBE9E7] text-[#7B6157] inline-flex items-center justify-center"><Pencil size={13} /></button>
                    <button type="button" data-testid={`del-los-${i}`} aria-label="Delete level of service" onClick={() => set({ levels_of_service: levels.filter((_, j) => j !== i) })} className="h-8 w-8 rounded-lg bg-[#FFEBEE] text-[#C62828] inline-flex items-center justify-center"><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {levels.length === 0 && <Empty text="No levels of service yet" />}
      </div>

      <Modal open={modal} onClose={() => setModal(false)} title={`${editIdx === null ? "Add" : "Edit"} Program/Service`} wide>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <FLabel>Program Name</FLabel>
            <FSelect data-testid="program-name" value={p.program_name} onChange={(e) => setP({ ...p, program_name: e.target.value })}>
              <option value="">Select a program…</option>
              {programList.map((n) => <option key={n} value={n}>{n}</option>)}
              {p.program_name && !programList.includes(p.program_name) && <option value={p.program_name}>{p.program_name}</option>}
            </FSelect>
            {!addingNew ? (
              <button type="button" data-testid="program-add-new-toggle" onClick={() => setAddingNew(true)}
                className="mt-1.5 text-xs font-semibold text-[#7B6157] hover:text-[#4E342E] underline">+ Create a new program</button>
            ) : (
              <div className="mt-2 flex gap-2">
                <FInput data-testid="program-new-name" placeholder="New program name…" value={newProg} onChange={(e) => setNewProg(e.target.value)} />
                <Btn type="button" data-testid="program-new-save" className="px-4 py-2 text-xs" onClick={createProgram} disabled={!newProg.trim()}>Create</Btn>
                <Btn type="button" variant="ghost" className="px-3 py-2 text-xs" onClick={() => { setAddingNew(false); setNewProg(""); }}>Cancel</Btn>
              </div>
            )}
          </div>
          <div><FLabel>Location</FLabel><FInput value={p.location} onChange={(e) => setP({ ...p, location: e.target.value })} /></div>
          <div><FLabel>Incident</FLabel><FInput value={p.incident} onChange={(e) => setP({ ...p, incident: e.target.value })} /></div>
          <div><FLabel>Program Status</FLabel><FSelect value={p.status} onChange={(e) => setP({ ...p, status: e.target.value })}>{Opt(PROGRAM_STATUSES)}</FSelect></div>
          <div><FLabel>Team</FLabel><FInput value={p.team} onChange={(e) => setP({ ...p, team: e.target.value })} /></div>
          <div><FLabel>Start Date</FLabel><DateField value={p.start_date} onChange={(v) => setP({ ...p, start_date: v })} disabledAfter={p.end_date || undefined} /></div>
          <div><FLabel>End Date</FLabel><DateField value={p.end_date} onChange={(v) => setP({ ...p, end_date: v })} disabledBefore={p.start_date || undefined} /></div>
          {PROGRAM_DATE_COLS.map(([k, label]) => (
            <div key={k}><FLabel>{label}</FLabel><DateField testId={`program-${k}`} value={p[k] || ""} onChange={(v) => setP({ ...p, [k]: v })} /></div>
          ))}
          <div><FLabel>Provider</FLabel><FInput value={p.provider} onChange={(e) => setP({ ...p, provider: e.target.value })} /></div>
          <div><FLabel>Auth. Confirmation #</FLabel><FInput value={p.auth_confirmation} onChange={(e) => setP({ ...p, auth_confirmation: e.target.value })} /></div>
          <div className="col-span-2 flex justify-end gap-2"><Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn>
            <Btn data-testid="program-save" onClick={saveProgram}>{editIdx === null ? "Add" : "Save"}</Btn></div>
        </div>
      </Modal>

      <Modal open={losModal} onClose={() => setLosModal(false)} title={`${losEditIdx === null ? "Add" : "Edit"} Level of Service (HLOC)`} wide>
        <div className="grid grid-cols-2 gap-4">
          <div><FLabel>Level of Service</FLabel><FSelect data-testid="los-level" value={los.level_of_service} onChange={(e) => setLos({ ...los, level_of_service: e.target.value })}>{Opt(LOS_LEVELS)}</FSelect></div>
          {LOS_FIELDS.map(([k, label, kind, opts]) => (
            <div key={k}>
              <FLabel>{label}</FLabel>
              {kind === "select" ? (
                <FSelect data-testid={`los-${k}`} value={los[k]} onChange={(e) => setLos({ ...los, [k]: e.target.value })}>
                  <option value="">Select…</option>{Opt(opts)}
                </FSelect>
              ) : (
                <FInput data-testid={`los-${k}`} value={los[k]} onChange={(e) => setLos({ ...los, [k]: e.target.value })} />
              )}
            </div>
          ))}
          <div className="col-span-2"><FLabel>Service Description</FLabel><FInput data-testid="los-service_description" value={los.service_description} onChange={(e) => setLos({ ...los, service_description: e.target.value })} /></div>
          <div className="col-span-2 flex justify-end gap-2"><Btn variant="ghost" onClick={() => setLosModal(false)}>Cancel</Btn>
            <Btn data-testid="los-save" onClick={saveLos}>{losEditIdx === null ? "Add" : "Save"}</Btn></div>
        </div>
      </Modal>
    </div>
  );
}

